import Form from 'react-bootstrap/Form';
import { ReactNode } from 'react';
import { useField } from 'formik';
import { isString, trim } from 'lodash';
import { DateTime } from 'luxon';
import SummaryDisplay from '../../SummaryDisplay';
import ComplexInlineHelp from '../../forms/ComplexInlineHelp';

export interface TextInputProps {
    label: string;
    name: string;
    inlineHelp?: string | ReactNode;
    inlineHelpTitle?: string;
    disabled?: boolean;
    readonly?: boolean;
    id?: string;
    type?: string;
    autoComplete?: string;
    placeholder?: string;
    containerClassName?: string;
    className?: string;
    isSummary?: boolean;
}

const TextInput = ({
    label,
    inlineHelp,
    inlineHelpTitle,
    disabled,
    readonly,
    name,
    id,
    type,
    autoComplete,
    placeholder,
    containerClassName = '', // default props
    className = '', // default props
    isSummary,
}: TextInputProps) => {
    const [_field, _meta, _fieldHelper] = useField(name);
    const helpId = inlineHelp ? `help-${id || name}` : undefined;

    const getDateTime = (date: Date) => DateTime.fromObject(
        { year: date.getFullYear(), month: date.getMonth(), day: date.getDay() },
    );

    const formattedValue = type === 'date' && _meta.value
        ? getDateTime(_meta.value).toFormat('yyyy-MM-DD')
        : _meta.value;

    if (isSummary) {
        const displayValue = type === 'date' && _field.value
            ? getDateTime(_field.value).toFormat('DD/MM/yyyy')
            : _field.value;

        return (
            <SummaryDisplay
                label={label}
                value={displayValue}
                as='p'
                id={id || name}
            />
        );
    }

    const handleOnBlur = () => {
        let newValue = type === 'date' && _meta.value ? getDateTime(_meta.value).toFormat('yyyy-MM-DD') : _meta.value;
        if (isString(_meta.value)) {
            newValue = trim(_meta.value);
        }
        _fieldHelper.setValue(newValue);
        _fieldHelper.setTouched(true);
    };

    return (
        <Form.Group className={`form-field-container ${containerClassName}`} controlId={id || name}>
            <Form.Label>{label}</Form.Label>
            {inlineHelp && !inlineHelpTitle
              && (
                  <Form.Text as='p' id={helpId} className='contextual-help'>
                      {inlineHelp}
                  </Form.Text>
              )}
            {inlineHelp && inlineHelpTitle
              && (
                  <ComplexInlineHelp id={helpId} title={inlineHelpTitle} inlineHelp={inlineHelp} />
              )}
            <Form.Control
                className={`form-field form-text-input ${className}`}
                type={type || 'text'}
                {..._field}
                isInvalid={!!(_meta.touched && _meta.error)}
                disabled={disabled}
                readOnly={readonly}
                aria-describedby={
                    _meta.touched && _meta.error
                        ? `${id || name}-validation-msg`
                        : (helpId || undefined)
                }
                value={formattedValue || ''}
                autoComplete={autoComplete}
                onBlur={handleOnBlur}
                placeholder={type === 'date' ? 'dd/mm/yyyy' : placeholder}
            />
            {_meta.touched && _meta.error
                ? (
                    <Form.Control.Feedback type='invalid' id={`${id || name}-validation-msg`} className='form-validation-message'>
                        {_meta.error}
                    </Form.Control.Feedback>
                )
                : null}
        </Form.Group>
    );
};

// TextInput.defaultProps = {
//     containerClassName: '',
//     className: '',
// };

export default TextInput;
